<template lang="html">
    <div class="p-2 group">
        <div class="flex" v-show="peer.role != 'controls'">
            <sound-meter ref="soundMeter" :id="(peer.public_key || 'unknown').toString()" :stream="stream" class="mr-1 h-8 w-1" v-show="showSoundmeter" v-on:speaking="onSpeaking()"></sound-meter>
            <sound-meter ref="secondarySoundMeter" :id="'sec_' + (peer.public_key || 'unknown').toString()" :stream="secondaryStream" class="mr-2" v-show="secondaryStream"></sound-meter>
            <div class="flex justify-between flex-grow" :class="peerStatusMap[peer.status]">
                <div class="text-sm font-medium text-gray-600 leading-none">
                    <div class="flex">
                        <div class="flex-grow">
                        {{peer.name}}
                        </div>
                        <div>
                            <span class="ml-1 uppercase text-xs font-semibold flex items-center status"
                                  :class="statusMap[peer.status]"
                                  :title="peer.status">
                                <!-- Circle icon -->
                                <svg class="h-3 w-3 inline-block fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10 3a7 7 0 1 0 .001 13.999A7 7 0 0 0 10 3z"/></svg>
                            </span>
                        </div>
                    </div>
                    <div class="font-medium text-gray-400 role capitalize">{{ peer.role }}</div>
                </div>
                <div v-if="isLoadingVideo">
                  <svg class="inline-block animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                   Loading...{{ loadingPercentage }}%</div>
                <div v-if="loadingPercentage == 100">
                  <!-- Check in a circle -->
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 text-green-500 inline-block">
                    <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clip-rule="evenodd" />
                  </svg>

                   Ready </div>
                <div class="flex items-center" v-show="showActions">
                  <div v-if="audio_track">
                    BR: {{ audio_track.bitrate }}
                  </div>
                    <button class="underline text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" @click='proxyHandleRemoteHangup(peer.public_key)' data-tippy-content="Disconnect user">
                        <!-- Close/times icon -->
                        <svg class="h-6 w-6 fill-current" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z" clip-rule="evenodd"/></svg>
                    </button>
                    <button class="underline text-green-500" @click='connectPeer()' :class="{'hidden': peer.status != 'waiting', 'btn-disabled': callingPeer}" :disabled="callingPeer">
                        Connect
                    </button>
                    <div class="text-yellow-500" :class="{'hidden': peer.status != 'connecting'}">
                        Connecting
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-white p-2">
            <div class="flex flex-wrap justify-between items-center hidden">
                <div class="w-full">
                    <audio :id="'aux_audio_'+ peer.public_key"          class="w-full" controls autoplay playsinline :muted="isMuted" :data-role="peer.role"></audio>
                    <audio :id="'audio_'+ peer.public_key"              class="w-full" controls autoplay playsinline :muted="isMuted" :data-role="peer.role"></audio>
                    <audio :id="'director_to_set_'+ peer.public_key"    class="w-full" controls autoplay playsinline :muted="isMuted" :data-role="peer.role"></audio>
                    <audio :id="'camera_audio_'+ peer.public_key"       class="w-full" controls autoplay playsinline :muted="isMuted" :data-role="peer.role"></audio>
                </div>
            </div>
            <div class="flex justify-between" v-show="showActions">
            	<div class="flex flex-wrap space-x-2 items-center">
                    <div>
                        AEC ON
                    </div>
                    <div class="form-switch toggleAECCommand" :data-id='peer.public_key'
                         data-tippy-content="Turn this on if the user has headphones to improve the quality of their audio.">
                        <input type="checkbox" checked>
                        <i></i>
                    </div>
                </div>
            <!-- anywhere specific actions for peer -->
            <div class="flex justify-end space-x-1 mt-2" v-show="peer.role == 'talent'">
                <button class="btn btn-sm"
                    :class='{"btn-disabled": uploadStatus != "uploads_available",
                             "btn-primary": uploadStatus == "uploads_available",
                             "uploading": uploadStatus == "uploading"}'
                    :disabled="uploadStatus != 'uploads_available' || uploadStatus == 'uploading' " @click='sendUpload'>
                    <svg class="hidden h-8 w-8 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M10 3v2a5 5 0 0 0-3.54 8.54l-1.41 1.41A7 7 0 0 1 10 3zm4.95 2.05A7 7 0 0 1 10 17v-2a5 5 0 0 0 3.54-8.54l1.41-1.41zM10 20l-4-4 4-4v8zm0-12V0l4 4-4 4z"/>
                    </svg>
                    <span v-show="uploadStatus != 'uploading' && uploadStatus != 'uploaded'"> Upload </span>
                    <span v-show="uploadStatus == 'uploaded'"> Uploaded </span>
                </button>
                <button class="btn btn-sm btn-red" @click='startRecording' v-show="!isRecording">
                    Record
                </button>
                <button class="btn btn-sm btn-red" @click='stopRecording' v-show="isRecording">
                    Stop recording
                </button>
                <button class="btn btn-sm btn-secondary muteButton" @click='mutePeer'>
                    {{ isMuted ? 'Unmute' : 'Mute' }}
                </button>
            </div>
            </div>
            <!-- End of justify between -->
        </div>
    </div>
</template>
<script>

import { localNotify } from 'utils/notifications'
import messageBroker from 'general/messageBroker';

import tippy from 'tippy.js';
import { getPeerStatusMap, getStatusMap, connectPeer, kickPeer } from "common/participant"

export default {
  props: ['peer', 'showActions'],
  data() {
    return {
      stream: null,
      secondaryStream: null,
      showCross: false,
      showStats: false,

      // Anywhere specific
      isRecording: false,
      uploadStatus: 'no_uploads',
      isMuted: false,
      isLoadingVideo: false,
      loadingPercentage: 0,

      audio_track: null,

      showSoundmeter: true,
      callingPeer: false,
      statusMap: {},
      peerStatusMap: {},
    }
  },
  mounted() {
    this.role = role; // Global role, setting it tolocal

    if(this.role == "guest" && this.peer.role == "talent" || 
       this.role == "talent" && this.peer.role == "guest"){
      this.showSoundmeter = false;
    }
    // hide tippy tooltips on mobile
    if (window.innerWidth > 768){
      tippy('[data-tippy-content]', { allowHTML: true, });
    }
    this.statusMap = getStatusMap();
    this.peerStatusMap = getPeerStatusMap();

    if (this.role == "host"){
    messageBroker.registerNewMessageType("anywhere--loading-video-status", (data) => {
      console.log("anywhere--loading-video-status", data)
      if(data.message.public_key != this.peer.public_key){
        return
      }
      if (data.message.percentage != 100){
        this.isLoadingVideo = true
        this.loadingPercentage = data.message.percentage
      } else{
        this.isLoadingVideo = false
        this.loadingPercentage = 100
      }
    });
  }
  },
  methods: {
    addStream(stream){
      this.stream = stream;
    },
    addSecondaryStream(stream){
      this.secondaryStream = stream;
    },
    proxyHandleRemoteHangup(peerKey){
      kickPeer(window.room_key, peerKey)
    },

    setUploadAvailable(){
      this.uploadStatus = 'uploads_available';
    },
    startRecording(){
      messageBroker.sendCommand("anywhere--start_recording", this.peer.public_key)
    },
    stopRecording(){
      messageBroker.sendCommand("anywhere--stop_recording", this.peer.public_key)
    },
    mutePeer(){
      $(`#audio_${this.peer.public_key}`)[0].muted = this.isMuted;
      this.isMuted = !this.isMuted;
    },
    handleFinishUpload(){
      localNotify("Finished uploading (" + this.peer.name + ")", "success")
      this.uploadStatus = 'uploaded'
    },
    sendUpload(){
      this.uploadStatus = 'uploading';
      messageBroker.sendCommand("anywhere--start_upload", this.peer.public_key)
    },
    updateStatus(){
      this.peer
    },
    updateStats(stats){
      for (const [key, track] of Object.entries(stats.inbound_tracks)) {
        if (track.bitrate > 0){
          this.audio_track = track; 
        }
      }
    },
    updatePeerInfo(data){
      const message = data.message;
      this.peer.download_speed = message.downloadSpeed;
      this.peer.upload_speed = message.uploadSpeed;
      // Anywhere specific
      this.isRecording = message.isRecording
      this.peer.audio_input = message.selectedInput;
    },
    connectPeer(){
      this.peer.status = 'connecting'
      this.callingPeer = true;
      window.setTimeout(() => {
        this.callingPeer = false;
      }, 100)

      connectPeer(window.room_key, this.peer.public_key);
    },
    onSpeaking(){
      this.$emit('speaking', this.peer.public_key)
    }
  },

}
</script>
<style lang="sass">
  .status{
  transition: all 0.2s ease-in-out;
  }

</style>